import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAppointments,
  rescheduleAppointment,
  cancelAppointment,
} from "./appointmentApi";

const initialState = {
  upcomingAppointments: [],
  todayAppointments: [],
  completedAppointments: [],
  pastAppointments: [],
  data: [], // current active data (based on selected tab)
  success: false,
  message: "",
  isLoading: false,
  error: null,
  activeTab: "today",
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    hasMore: false,
  },
};

export const appointmentSlice = createSlice({
  name: "appointments",
  initialState,
  reducers: {
    clearAppointmentError: (state) => {
      state.error = null;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      // Update data based on active tab
      if (action.payload === "today") {
        state.data = state.todayAppointments;
      } else if (action.payload === "upcoming") {
        state.data = state.upcomingAppointments;
      } else if (action.payload === "past") {
        state.data = state.pastAppointments;
      }
    },
    resetPagination: (state) => {
      state.pagination = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
        hasMore: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAppointments.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAppointments.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = action.payload.success;
      state.message = action.payload.message;
      state.error = null;

      const appointmentStatus = action.meta?.arg?.appointmentStatus;
      const data = action.payload.data || [];

      // Store data based on appointment status
      if (appointmentStatus === "UPCOMING") {
        state.upcomingAppointments = data;
        if (state.activeTab === "upcoming") {
          state.data = data;
        }
      } else if (appointmentStatus === "TODAY") {
        state.todayAppointments = data;
        if (state.activeTab === "today") {
          state.data = data;
        }
      } else if (appointmentStatus === "COMPLETED") {
        state.completedAppointments = data;
        if (state.activeTab === "past") {
          state.data = data;
        }
      } else if (appointmentStatus === "PAST") {
        state.pastAppointments = data;
        if (state.activeTab === "past") {
          state.data = data;
        }
      }

      // Handle pagination data
      const pagination = action.payload.pagination;
      if (pagination) {
        const { totalCount, hasMore } = pagination;
        const currentPage = action.meta?.arg?.skip
          ? Math.floor(action.meta.arg.skip / (action.meta?.arg?.limit || 10)) +
            1
          : 1;
        const pageSize = action.meta?.arg?.limit || 10;

        state.pagination = {
          currentPage,
          pageSize,
          totalItems: totalCount,
          totalPages: Math.ceil(totalCount / pageSize),
          hasMore,
        };
      }
    });
    builder.addCase(fetchAppointments.rejected, (state, action) => {
      state.isLoading = false;
      state.success = false;
      state.message = "";
      state.error = action.payload?.message || "Failed to fetch appointments";
    });

    // Reschedule appointment
    builder.addCase(rescheduleAppointment.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(rescheduleAppointment.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = action.payload.success;
      state.message = action.payload.message;
      state.error = null;
      // Refresh the appointments data after reschedule
      // This will trigger a re-fetch of the current tab's data
    });
    builder.addCase(rescheduleAppointment.rejected, (state, action) => {
      state.isLoading = false;
      state.success = false;
      state.error =
        action.payload?.message || "Failed to reschedule appointment";
    });

    // Cancel appointment
    builder.addCase(cancelAppointment.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(cancelAppointment.fulfilled, (state, action) => {
      state.isLoading = false;
      state.success = action.payload.success;
      state.message = action.payload.message;
      state.error = null;
      // Refresh the appointments data after cancel
      // This will trigger a re-fetch of the current tab's data
    });
    builder.addCase(cancelAppointment.rejected, (state, action) => {
      state.isLoading = false;
      state.success = false;
      state.error = action.payload?.message || "Failed to cancel appointment";
    });
  },
});

export const { clearAppointmentError, setActiveTab, resetPagination } =
  appointmentSlice.actions;
export default appointmentSlice.reducer;
